// ============================================
// SCHEDULE SERVICE - Gestión de horarios de atención
// ============================================

import { initFirebase } from "./firebaseClient.js";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

// Días de la semana (0 = lunes, 6 = domingo en nuestra estructura)
export const DIAS_SEMANA = {
  0: "Lunes",
  1: "Martes",
  2: "Miércoles",
  3: "Jueves",
  4: "Viernes",
  5: "Sábado",
  6: "Domingo",
};

export const DIAS_SEMANA_SHORT = {
  0: "Lun",
  1: "Mar",
  2: "Mie",
  3: "Jue",
  4: "Vie",
  5: "Sab",
  6: "Dom",
};

// Estructura de datos de horarios
// {
//   bloques: [
//     {
//       dias: [0, 1, 2, 3, 4], // Lun a Vie
//       inicio: "20:00",
//       fin: "00:00"
//     },
//     {
//       dias: [5, 6], // Sab y Dom
//       inicio: "12:00",
//       fin: "01:00"
//     }
//   ],
//   activo: true,
//   updatedAt: timestamp
// }

/**
 * Obtiene los horarios de atención desde Firestore
 */
export const getSchedule = async () => {
  try {
    const db = initFirebase();
    if (!db) return null;

    const scheduleRef = doc(db, "config", "schedule");
    const scheduleDoc = await getDoc(scheduleRef);

    if (scheduleDoc.exists()) {
      return scheduleDoc.data();
    }

    // Si no existe, retornar horarios por defecto
    return getDefaultSchedule();
  } catch (error) {
    console.error("Error al obtener horarios:", error);
    return getDefaultSchedule();
  }
};

/**
 * Guarda los horarios en Firestore
 */
export const saveSchedule = async (schedule) => {
  try {
    const db = initFirebase();
    if (!db) throw new Error("Firebase no disponible");

    const scheduleRef = doc(db, "config", "schedule");
    await setDoc(scheduleRef, {
      ...schedule,
      updatedAt: serverTimestamp(),
    });

    return schedule;
  } catch (error) {
    console.error("Error al guardar horarios:", error);
    throw error;
  }
};

/**
 * Retorna horarios por defecto
 */
export const getDefaultSchedule = () => {
  return {
    bloques: [
      {
        dias: [0, 1, 2, 3, 4], // Lun a Vie
        inicio: "20:00",
        fin: "00:00",
      },
      {
        dias: [5, 6], // Sab y Dom
        inicio: "12:00",
        fin: "01:00",
      },
    ],
    activo: true,
  };
};

const ARGENTINA_TZ = "America/Argentina/Buenos_Aires";

/**
 * Obtiene hora y día actual en zona horaria Argentina
 */
function getNowInArgentina() {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("es-AR", {
    timeZone: ARGENTINA_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    weekday: "short",
  });
  const parts = formatter.formatToParts(now);
  let hours = "0",
    minutes = "0",
    weekday = "dom";
  parts.forEach((p) => {
    if (p.type === "hour") hours = p.value;
    if (p.type === "minute") minutes = p.value;
    if (p.type === "weekday") weekday = p.value.replace(".", "").toLowerCase().slice(0, 3);
  });
  const dayMap = { lun: 0, mar: 1, mie: 2, mié: 2, jue: 3, vie: 4, sab: 5, sáb: 5, dom: 6 };
  const adjustedDay = dayMap[weekday] ?? 0;
  const currentTime = `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
  return { adjustedDay, currentTime };
}

/**
 * Verifica si ahora es horario de atención (usa hora Argentina)
 */
export const isOpenNow = (schedule = null) => {
  if (!schedule) return true; // Si no hay horarios configurados, asumimos que está abierto

  if (!schedule.activo) return false;

  const { adjustedDay, currentTime } = getNowInArgentina();

  for (const bloque of schedule.bloques) {
    if (bloque.dias.includes(adjustedDay)) {
      return isTimeBetween(currentTime, bloque.inicio, bloque.fin);
    }
  }

  return false;
};

/**
 * Verifica si una hora está entre inicio y fin
 * Maneja casos donde el fin es al día siguiente (ej: 20:00 a 01:00)
 */
export const isTimeBetween = (current, start, end) => {
  const currentMinutes = timeToMinutes(current);
  const startMinutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);

  // Si el fin es después del inicio (ej: 20:00 a 00:00)
  if (endMinutes <= startMinutes) {
    // La hora es válida si es >= inicio O <= fin (del día siguiente)
    return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
  }

  // Si el fin es después del inicio (ej: 09:00 a 18:00)
  return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
};

/**
 * Convierte formato HH:MM a minutos desde medianoche
 */
export const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

/**
 * Convierte minutos a formato HH:MM
 */
export const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

/**
 * Formatea los horarios para mostrar al usuario
 */
export const formatScheduleDisplay = (schedule) => {
  if (!schedule || !schedule.bloques) return "Horarios no configurados";

  return schedule.bloques
    .map((bloque) => {
      const dias = bloque.dias
        .map((d) => DIAS_SEMANA_SHORT[d])
        .join(" ");
      return `${dias} de ${bloque.inicio} a ${bloque.fin}`;
    })
    .join(" | ");
};

/**
 * Obtiene el horario para un día específico
 */
export const getScheduleForDay = (schedule, dayIndex) => {
  if (!schedule || !schedule.bloques) return null;
  return schedule.bloques.find((bloque) => bloque.dias.includes(dayIndex));
};

/**
 * Obtiene el mensaje de horario actual
 */
export const getStatusMessage = (schedule) => {
  if (!schedule?.activo) {
    return "Cerrado momentáneamente";
  }

  if (isOpenNow(schedule)) {
    return "Abierto para pedidos";
  }

  return "Cerrado - Abre próximamente";
};
